import { useState } from "react";
import { CheckIcon, Undo2Icon } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { errorMessage } from "~/lib/api";
import { useResolvePaymentConflict, type PaymentConflict } from "~/lib/queries";
import { formatMinorIDR } from "./-format";

type Action = "accept" | "mark-refunded";

/**
 * Accept / Mark refunded for one open conflict (PRO-1345).
 *
 * Accept is the consequential one: it moves the transaction EXPIRED ->
 * SUCCESSFULL and sends the merchant payment.succeeded, so they will ship. The
 * dialog says so, and says the thing to check first -- that the merchant can
 * still fulfil the order. Mark refunded changes nothing but the conflict, so it
 * asks for the refund's reference instead.
 */
export function ResolveConflictActions({ conflict }: { conflict: PaymentConflict }) {
  const [action, setAction] = useState<Action | null>(null);
  const [note, setNote] = useState("");
  const resolve = useResolvePaymentConflict(conflict);

  const noteRequired = action === "mark-refunded";
  const close = () => {
    setAction(null);
    setNote("");
    resolve.reset();
  };

  return (
    <div className="flex justify-end gap-1.5">
      <Button
        size="sm"
        variant="outline"
        disabled={!conflict.acceptable || resolve.isPending}
        title={
          conflict.acceptable
            ? undefined
            : `The transaction is ${conflict.current_status ?? "missing"}, not EXPIRED, so this payment can only be refunded.`
        }
        onClick={() => setAction("accept")}
      >
        <CheckIcon className="size-3.5" />
        Accept
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={resolve.isPending}
        onClick={() => setAction("mark-refunded")}
      >
        <Undo2Icon className="size-3.5" />
        Mark refunded
      </Button>

      <ConfirmDialog
        open={action !== null}
        onOpenChange={(open) => (open ? undefined : close())}
        title={action === "accept" ? "Accept this late payment?" : "Mark this payment refunded?"}
        description={`${conflict.order_id} · ${conflict.psp_payment_id} · ${formatMinorIDR(conflict.amount_minor)}`}
        confirmLabel={action === "accept" ? "Accept payment" : "Mark refunded"}
        pending={resolve.isPending}
        onConfirm={() => {
          if (!action) return;
          if (noteRequired && note.trim() === "") return;
          resolve.mutate(
            { action, note: note.trim() },
            {
              onSuccess: () => {
                close();
                toast.success(
                  action === "accept"
                    ? "Accepted. The transaction is SUCCESSFULL and the merchant is being notified."
                    : "Marked refunded.",
                );
              },
            },
          );
        }}
        body={
          <>
            {action === "accept" ? (
              <>
                <p className="text-muted-foreground">
                  The transaction moves from EXPIRED to SUCCESSFULL and the merchant is sent
                  <code className="mx-1 font-mono">payment.succeeded</code>, so they will fulfil
                  the order.
                </p>
                <p className="rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
                  Check first that the merchant can still fulfil it: they were told this order
                  expired, and may have released its stock. If they cannot, refund the buyer and
                  use Mark refunded instead. This cannot be undone here.
                </p>
              </>
            ) : (
              <p className="text-muted-foreground">
                Records that the money was returned to the buyer outside Altura. The transaction
                and the merchant are not touched.
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor={`conflict-note-${conflict.id}`}>
                {noteRequired ? "Refund reference (required)" : "Note (optional)"}
              </Label>
              <Input
                id={`conflict-note-${conflict.id}`}
                value={note}
                maxLength={1000}
                placeholder={
                  noteRequired
                    ? "e.g. refunded via DurianPay dashboard, RF-12345"
                    : "e.g. stock confirmed with the merchant"
                }
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
            {resolve.error ? (
              <p className="text-xs text-destructive">{errorMessage(resolve.error)}</p>
            ) : null}
          </>
        }
      />
    </div>
  );
}
